import React, { useState } from "react";
import { Button, Flex, Form, Input, Modal, notification } from "antd";
import Icon from "@mdi/react";
import { mdiArrowLeft, mdiCheckCircle, mdiClose, mdiEmailOutline } from "@mdi/js";
import CustomerApi from "../../api-services/customer-api.service";
import { useAuth } from "../../services/AuthContext";

const validateMessages = {
  required: '${label} is required!',
  types: {
    email: '${label} is not a valid email!'
  },
};

type FieldType = {
  email?: string;
  password?: string;
};

type LinkFieldType = {
  email?: string;
};

type SignInMode = "password" | "link-request" | "link-sent";

const SignInForm: React.FC<{ onSuccess?: () => void; showAuthLinkOption?: boolean; onClose?: () => void, isV3?: boolean}> = ({ onSuccess, showAuthLinkOption, onClose, isV3 = false}) => {
  const { login } = useAuth();
  const [signInForm] = Form.useForm<FieldType>();
  const [linkForm] = Form.useForm<LinkFieldType>();
  const [submitting, setSubmitting] = useState(false);
  const [forgotPasswordModalVisible, showForgotPasswordModal] = useState(false);
  const [mode, setMode] = useState<SignInMode>("password");
  const [linkEmail, setLinkEmail] = useState("");
  const [linkSubmitting, setLinkSubmitting] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);

  const closeModal = () => {
    showForgotPasswordModal(false);
  };

  const sendRequest = (formValues: { email: string }) => {
    CustomerApi.forgotPassword(formValues.email)
      .then(success => {
        if (success) {
          closeModal();
          Modal.success({
            content: "If your email exists in our database, you will receive a password recovery link at your email address in a few minutes."
          });
        }
      })
      .catch(error => {
        notification.error({ message: error.response?.data?.error || error.message || "An error occurred" });
      });
  };
  
  const handleFieldsChange = async () => { 
    const hasErrors = signInForm.getFieldsError().some(({ errors }) => errors.length > 0);
    const values = signInForm.getFieldsValue();
    const hasRequiredValues = !!values.email?.trim() && !!values.password?.trim();
    setIsFormValid(hasRequiredValues && !hasErrors);
  };

  const handleFinish = (values: FieldType) => {
    setSubmitting(true);
    login(values)
      .then(() => onSuccess?.())
      .catch(() => { /* already toasted inside login() */ })
      .finally(() => setSubmitting(false));
  };

  const submitLinkRequest = (values: LinkFieldType) => {
    setLinkSubmitting(true);
    CustomerApi.requestLink(values)
      .then(() => {
        setLinkEmail(values.email || "");
        setMode("link-sent");
      })
      .catch(error => {
        notification.error({ message: error.response?.data?.error || error.message || "An error occurred" });
      })
      .finally(() => setLinkSubmitting(false));
  };

  const resendLink = () => {
    CustomerApi.requestLink({ email: linkEmail })
      .then(() => {
        notification.success({ message: "Link sent again — check your inbox." });
      })
      .catch(error => {
        notification.error({ message: error.response?.data?.error || error.message || "An error occurred" });
      });
  };

  if (mode === "link-request") {
    return (
      <div className="signin-link-form">
        <button type="button" className="signin-drawer_close" aria-label="Back" onClick={() => setMode("password")}>
          <Icon path={mdiArrowLeft} size="24px" color="#000" />
        </button>
        <h2 className="signin-link-form_title">What's your email?</h2>
        <p className="signin-link-form_subtitle">We'll send you an authentication link to login.</p>
        <Form form={linkForm} autoComplete="off" layout="vertical" variant="outlined" validateMessages={validateMessages} onFinish={submitLinkRequest}>
          <Form.Item<LinkFieldType> label="Email" name="email" rules={[{ required: true }, { type: "email" }]}>
            <Input placeholder="e.g. joshua@gmail.com" size="large" />
          </Form.Item>
          <Form.Item noStyle shouldUpdate>
            {() => (
              <Button
                type="primary"
                htmlType="submit"
                block
                loading={linkSubmitting}
                disabled={!linkForm.isFieldsTouched(true) || linkForm.getFieldsError().some(({ errors }) => errors.length > 0)}
              >
                Send Link
              </Button>
            )}
          </Form.Item>
        </Form>
      </div>
    );
  }

  if (mode === "link-sent") {
    return (
      <div className="signin-link-sent">
        <button type="button" className="signin-drawer_close" aria-label="Close" onClick={onClose}>
          <Icon path={mdiClose} size="24px" color="#1E1E1E" />
        </button>
        <div className="signin-link-sent_icon">
          <Icon path={mdiCheckCircle} size="28px" color="#1a9c4b" />
        </div>
        <h2 className="signin-link-sent_title">Link sent!</h2>
        <p className="signin-link-sent_body">
          We've emailed a sign-in link to <b>{linkEmail}</b>. Opening it signs you in from a new tab — come back and continue here once you're signed in.
        </p>
        <button type="button" className="signin-link-sent_resend" onClick={resendLink}>Didn't get it? Resend link</button>
      </div>
    );
  }

  return (
    <>
      {showAuthLinkOption && (
        <>
          <button type="button" className="signin-drawer_close" aria-label="Close" onClick={onClose}>
            <Icon path={mdiClose} size="24px" color="#1E1E1E" />
          </button>
          <div className="signin-form_header">
            <h2 className="signin-form_title">Welcome back!</h2>
            <p className="signin-form_subtitle">Please login to continue</p>
          </div>
        </>
      )}

      <Form
        form={signInForm}
        autoComplete="off"
        layout="vertical"
        variant={showAuthLinkOption ? "outlined" : "filled"}
        onFinish={handleFinish}
        onFieldsChange={handleFieldsChange}
        className="sign-in-form"
      >
        <Form.Item<FieldType>
          label={isV3 ? "Email" : "Email Address"}
          name="email"
          rules={[{ required: true, message: 'Please input your email address' }, { type: "email" }]}
        >
          <Input placeholder={isV3 ? "e.g. joshua@gmail.com" :"Enter Email Address"} size={showAuthLinkOption ? "large" : undefined} />
        </Form.Item>

        <Form.Item<FieldType>
          label="Password"
          name="password"
          rules={[{ required: true, message: 'Please input your password' }]}
        >
          <Input.Password placeholder="Enter Password" size={showAuthLinkOption ? "large" : undefined} />
        </Form.Item>

        <p id="forgot-password" onClick={() => showForgotPasswordModal(true)}>Forgot password?</p>

        <Flex align="center" justify="center">
          {showAuthLinkOption ? (
            <Form.Item noStyle shouldUpdate>
              {() => (
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={submitting}
                  disabled={!signInForm.isFieldsTouched(true) || signInForm.getFieldsError().some(({ errors }) => errors.length > 0)}
                >
                  Sign In
                </Button>
              )}
            </Form.Item>
          ) : (
            <Button type="primary" htmlType="submit" disabled={!isFormValid} loading={submitting}>
              Sign In
            </Button>
          )}
        </Flex>

        {showAuthLinkOption && (
          <>
            <div className="signin-divider"><span>Or</span></div>
            <button type="button" className="signin-link-toggle" onClick={() => setMode("link-request")}>
              <Icon path={mdiEmailOutline} size="18px" color="#5D5F6A" />
              Login via authentication link
            </button>
          </>
        )}
      </Form>

      <Modal title={
        <>
          <h2>Forgot Password</h2>
          <p>To reset your password, please enter your email address</p>
        </>
      } open={forgotPasswordModalVisible} onCancel={closeModal} footer={null} width={800} centered className="forgot_password_modal">
        <Form
          autoComplete="off"
          layout="vertical"
          variant="filled"
          validateMessages={validateMessages}
          onFinish={sendRequest}
          className="forgot-password-form"
        >
          <Form.Item label="Email" name="email" required>
            <Input placeholder="Enter Email" />
          </Form.Item>

          <Flex align="center" justify="center">
            <Button type="primary" htmlType="submit">
              Submit
            </Button>
          </Flex>
        </Form>
      </Modal>
    </>
  );
};

export default SignInForm;
